import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { RefreshCw, Ticket } from 'lucide-react'
import { Tabs, useToast } from '../admin/primitives.js'
import { guardedClose } from '../shell/editing.js'
import { SmartBack } from '../navigation/SmartBack.js'
import { setPageTitle } from '../prefs.js'
import { remplacerRecherche } from '../finance/adresse-ecran.js'
import { businessNightDayISO } from '../soirees/business-night.js'
import { BandeauSoiree, Chiffre } from '../finance/ClosingHero.js'
import '../finance/finance-wizard.css'
import { chargerFiche, type Resultat } from './soirees-maison-api.js'
import { useLecture, useMoi, usePreferencesBilletterie } from './soirees-maison-hooks.js'
import {
  GardeCles, NOM_BILLETTERIE, PHRASE_PASSEE_EN_LIGNE, STATUT_LIBELLE,
  chiffresDuBandeau, compteARebours, heureDeNuitLisible, instantParis, lectureSeule, notesApresGeste, nuitEnLettres, proposeRecharger,
  raisonEcriture, stockageMemoire, stockageSession, suiteErreurAcces, tonStatut,
  type ErreurBM, type Fiche, type ResultatGeste, type Soiree,
} from './soirees-maison-model.js'
import { FicheGestes } from './FicheGestes.js'
import { FicheTarifs } from './FicheTarifs.js'
import { FicheSoireeForm } from './FicheSoireeForm.js'
import { FicheRemplissage, FicheVentes } from './FicheVentes.js'
import { GestesAffiche, visuelAffiche } from './AfficheSoiree.js'

/**
 * /soirees/billetterie/:id — LA FICHE D'UNE SOIRÉE DE BILLETTERIE LIVE CLUB.
 *
 * En-tête (état, pilule « Démo — ne compte nulle part »), gestes de vente,
 * puis trois onglets : Tarifs, La soirée, Ventes & entrées.
 *
 * L'en-tête est LE BANDEAU DE LA FICHE D'UNE NUIT EN COMPTABILITÉ
 * (`BandeauSoiree`, ClosingHero.tsx), sur toute la largeur — « comme
 * l'affichage dans comptabilité » (l'exploitant, 23/10/2026) : Retour et les
 * gestes de l'affiche en haut, l'affiche floutée en fond et nette à côté du
 * titre, quatre tuiles (vendus, valeur des billets, entrées, places restantes) ;
 * sans affiche, ses halos et l'icône. Dessous, DEUX COLONNES (« inspire-toi
 * de Shotgun », même jour) : les onglets à gauche, la mise en vente et le
 * remplissage à droite, qui restent en vue pendant qu'on défile. Le tout
 * dans une colonne de 1 180 px au centre, comme le tableau de bord d'un
 * événement Shotgun : plus rien ne s'étale sur un grand écran. Les gestes (ajouter, changer, retirer)
 * suivent les droits et l'état d'écriture comme les autres.
 *
 * Chaque geste DIT AVANT LE CLIC ce qu'il fera et ce qui deviendra impossible :
 * la billetterie rend ses `gestes` (possible, raison), ses `verrous` et ses
 * `avertissements` ; Gestion ses droits et son état d'écriture. Le bouton se
 * grise avec la première raison (droits, écriture, gestes). Le serveur reste
 * juge à chaque envoi : l'écran sait toujours afficher un refus.
 *
 * UNE unité d'édition à la fois (design system §12) : la Save Bar sert
 * l'onglet « La soirée » OU la modale d'un tarif, jamais les deux ; quitter
 * l'onglet avec des changements passe par « Modifications non enregistrées ».
 *
 * Et APRÈS le clic, ce que la billetterie a fait en plus du geste (tarifs
 * décalés avec la nuit, « l'heure d'hiver a été retenue », un tarif ouvert ou
 * refermé par la cascade) reste écrit sur la fiche jusqu'au geste suivant :
 * ces avertissements n'existent que dans la réponse de l'écriture.
 */

export type Onglet = 'tarifs' | 'soiree' | 'ventes'
const ONGLETS: { key: Onglet; label: string }[] = [
  { key: 'tarifs', label: 'Tarifs' },
  { key: 'soiree', label: 'La soirée' },
  { key: 'ventes', label: 'Ventes & entrées' },
]

/** Ce que chaque partie de la fiche reçoit pour écrire. */
export interface Ecrivain {
  soiree: Soiree
  fiche: Fiche
  /**
   * Un geste : la clé (gardée tant qu'aucune réponse 2xx n'est lue, réutilisée
   * seulement pour un corps identique), l'envoi, puis la fiche rendue. Rend
   * l'erreur à afficher, ou `null` en cas de succès.
   */
  executer: (o: {
    ecran: string
    corps: unknown
    envoi: (cle: string) => Promise<Resultat<Fiche>>
    /** Le message du succès ; d'après ce que la billetterie a vraiment fait, s'il en dépend (`inchange`, tarifs retirés). */
    succes: string | ((r: ResultatGeste | null) => string)
    /** Création (ajout d'un tarif) : clé en `sessionStorage`, survit au rechargement. */
    session?: boolean
    /** Geste de préparation envoyé sans droit de mise en vente (le brouillon a pu passer en vente). */
    preparationHorsLigne?: boolean
    /** La Save Bar dit déjà le succès : pas de second message. */
    silencieux?: boolean
  }) => Promise<ErreurBM | null>
  recharger: () => void
  signalerErreur: (e: ErreurBM | null) => void
}

function ongletDepuisAdresse(): Onglet {
  const o = typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('onglet') : null
  return o === 'soiree' || o === 'ventes' ? o : 'tarifs'
}

export function SoireeMaisonPage({ id }: { id: string }) {
  const toast = useToast()
  const moi = useMoi()
  const { prefs } = usePreferencesBilletterie()
  const charger = useCallback(() => chargerFiche(id), [id])
  const { lecture, recharger, poser } = useLecture(charger, id, prefs.rafraichissement_s)
  const [onglet, setOnglet] = useState<Onglet>(ongletDepuisAdresse)
  // L'onglet est dans l'adresse : un Retour ou un lien rouvre le même.
  useEffect(() => {
    remplacerRecherche(window.location.pathname, onglet === 'tarifs' ? new URLSearchParams() : new URLSearchParams({ onglet }))
  }, [onglet])
  const [bandeau, setBandeau] = useState<ErreurBM | null>(null)
  /** Ce que la billetterie a fait en plus du dernier geste réussi ; effacé au geste suivant. */
  const [suite, setSuite] = useState<string[]>([])
  const [relectureEchec, setRelectureEchec] = useState(false)
  const formulaireModifie = useRef(false)

  const memoire = useRef(stockageMemoire())
  const cles = useMemo(() => ({
    memoire: new GardeCles(memoire.current, moi ?? 'inconnu'),
    session: new GardeCles(stockageSession(), moi ?? 'inconnu'),
  }), [moi])

  const fiche = lecture.etat === 'ok' ? lecture.data : null
  const soiree = fiche?.soiree ?? null
  useEffect(() => { setPageTitle(soiree ? soiree.nom : NOM_BILLETTERIE) }, [soiree?.nom]) // eslint-disable-line react-hooks/exhaustive-deps

  const executer = useCallback<Ecrivain['executer']>(async (o) => {
    const garde = o.session ? cles.session : cles.memoire
    const cle = garde.pour(o.ecran, JSON.stringify(o.corps))
    setSuite([])
    const r = await o.envoi(cle)
    if (r.ok) {
      garde.oublier(o.ecran)
      setBandeau(null)
      if (r.data.soiree) { poser(r.data); setRelectureEchec(false) } else { setRelectureEchec(true); recharger() }
      // Durable, même quand la Save Bar dit déjà « enregistré » : un toast qui disparaît ne suffit pas.
      setSuite(notesApresGeste(r.data.resultat))
      const succes = typeof o.succes === 'function' ? o.succes(r.data.resultat) : o.succes
      if (!o.silencieux || r.data.rejouee) toast('ok', r.data.rejouee ? 'Déjà fait : la billetterie avait déjà enregistré ce geste.' : succes)
      return null
    }
    // §6.4, étape 6 : envoyé comme un geste de préparation, refusé parce que la soirée est passée en ligne.
    if (o.preparationHorsLigne && r.erreur.code === 'BILLETTERIE_INTERDIT') return { ...r.erreur, message: PHRASE_PASSEE_EN_LIGNE }
    return r.erreur
  }, [cles, poser, recharger, toast])

  const changerOnglet = (k: string) => {
    const suivant = k as Onglet
    if (suivant === onglet) return
    guardedClose(onglet === 'soiree' && formulaireModifie.current, () => {
      formulaireModifie.current = false
      setOnglet(suivant)
    }, 'Les changements de la soirée ne sont pas enregistrés. Si vous changez d’onglet maintenant sans enregistrer, ils seront perdus.')
  }

  if (lecture.etat === 'chargement') {
    return <main className="lc-page lc-stagger bs-page bs-page--fiche" aria-busy="true"><SmartBack fallback="/soirees/billetterie" /><div className="so-skel"><span /><span /><span /></div></main>
  }
  if (lecture.etat === 'erreur') {
    // Une coupure par la billetterie ou un blocage : la fiche ne reste pas à l'écran avec des boutons qui échoueraient tous.
    const acces = suiteErreurAcces(lecture.erreur)
    return (
      <main className="lc-page lc-page--readable lc-stagger bs-page">
        <SmartBack fallback="/soirees/billetterie" />
        <h1 className="lc-title bs-titre-icone"><Ticket size={26} aria-hidden="true" /> {NOM_BILLETTERIE}</h1>
        <div className={`bs-note${acces ? ' bs-note--ko' : ' bs-note--doute'}`} role="status">
          <span>{lecture.erreur.message}</span>
          {acces ? <span className="lc-hint">{acces}</span> : null}
        </div>
        <button type="button" className="lc-btn" onClick={recharger}><RefreshCw size={15} aria-hidden="true" /> Réessayer</button>
      </main>
    )
  }
  if (!fiche || !soiree) {
    return (
      <main className="lc-page lc-page--readable lc-stagger bs-page">
        <SmartBack fallback="/soirees/billetterie" />
        <p className="bs-note" role="status">Enregistré. La soirée n’a pas pu être relue : rechargez la page.</p>
        <button type="button" className="lc-btn" onClick={recharger}><RefreshCw size={15} aria-hidden="true" /> Recharger</button>
      </main>
    )
  }

  const ecrivain: Ecrivain = { soiree, fiche, executer, recharger, signalerErreur: setBandeau }
  const ton = tonStatut(soiree.statut)
  const ecriture = lectureSeule(fiche.actions) ? null : raisonEcriture(fiche.ecriture)
  const rebours = compteARebours(soiree.nuit, businessNightDayISO(Date.now()))
  const horaires = soiree.ouverture_heure && soiree.fermeture_heure
    ? `${heureDeNuitLisible(soiree.nuit, soiree.ouverture_heure)} → ${heureDeNuitLisible(soiree.nuit, soiree.fermeture_heure)}` : null

  return (
    <main className="lc-page lc-stagger bs-page bs-page--fiche">
      {/* Le cadre porte le conteneur des règles de largeur du bandeau (`@container fwz`) : lui seul, pas la page. */}
      <div className="bs-cadre">
        <BandeauSoiree
          soiree={{ id: soiree.id, name: soiree.nom }}
          visuel={visuelAffiche(soiree)}
          retour="/soirees/billetterie"
          icone={Ticket}
          surtitre={nuitEnLettres(soiree.nuit)}
          titre={soiree.nom}
          etat={(
            <>
              <span className={`lc-pill lc-pill--${ton}`}><span className={`lc-dot${ton === 'idle' ? '' : ` lc-dot--${ton}`}`} aria-hidden="true" />{STATUT_LIBELLE[soiree.statut]}</span>
              <span className="lc-pill lc-pill--warning"><span className="lc-dot lc-dot--warning" aria-hidden="true" />Démo — ne compte nulle part</span>
              {soiree.entrainement ? <span className="lc-pill lc-pill--idle">Soirée d’entraînement</span> : null}
              {soiree.complet_a ? <span className="lc-pill lc-pill--warning">Annoncée COMPLET {instantParis(soiree.complet_a)}</span> : null}
              {rebours.texte !== '—' ? <span className={`lc-pill${rebours.proche ? ' bs-rebours--proche' : ' lc-pill--idle'} bs-rebours`}>{rebours.texte}</span> : null}
            </>
          )}
          meta={(
            <>
              {horaires ? <span>Ouverture {horaires}</span> : null}
              {soiree.salles.length > 0 ? <span>{soiree.salles.map((x) => x.nom).join(' · ')}</span> : null}
              {soiree.cloture_auto_a ? <span>Clôture automatique le {instantParis(soiree.cloture_auto_a)}, définitive</span> : null}
              <span>Démo : ni calendrier, ni compta, ni Accueil</span>
            </>
          )}
          actions={lectureSeule(fiche.actions) ? null : <GestesAffiche ecrivain={ecrivain} />}
          chiffres={chiffresDuBandeau(soiree, fiche.recette_masquee).map((c) => <Chiffre key={c.libelle} libelle={c.libelle} valeur={c.valeur} aide={c.aide} />)}
        />
      </div>

      {relectureEchec ? <p className="bs-note" role="status">Enregistré. La soirée n’a pas pu être relue : rechargez la page.</p> : null}
      {ecriture ? <p className="bs-note" role="status">{ecriture}</p> : null}
      {lecture.erreur ? <p className="bs-note bs-note--doute" role="status">Dernière relecture en échec : {lecture.erreur.message}</p> : null}
      {bandeau ? (
        <div className="bs-note bs-note--ko" role="alert">
          <span>{bandeau.message}</span>
          {proposeRecharger(bandeau) ? <button type="button" className="lc-btn lc-btn--sm" onClick={() => { setBandeau(null); recharger() }}><RefreshCw size={14} aria-hidden="true" /> Recharger</button> : null}
        </div>
      ) : null}
      {suite.map((t) => <p key={t} className="bs-note bs-note--doute" role="status">{t}</p>)}
      {soiree.avertissements.map((a) => <p key={a.code} className="bs-note bs-note--doute">{a.message}</p>)}

      {/* Deux colonnes, comme la fiche d'une nuit en compta : le travail à gauche, la vente et le remplissage à droite.
          En étroit, la vente passe d'abord, puis les onglets. */}
      <div className="bs-fiche">
        <aside className="bs-fiche__cote" aria-label="Mise en vente et remplissage">
          <FicheGestes ecrivain={ecrivain} />
          <FicheRemplissage soiree={soiree} />
        </aside>
        <div className="bs-fiche__principal">
          <div className="bs-onglets">
            <Tabs tabs={ONGLETS} active={onglet} onChange={changerOnglet} />
          </div>
          {onglet === 'tarifs' ? <FicheTarifs ecrivain={ecrivain} versLaSoiree={() => changerOnglet('soiree')} /> : null}
          {onglet === 'soiree' ? <FicheSoireeForm ecrivain={ecrivain} onModifie={(m) => { formulaireModifie.current = m }} /> : null}
          {onglet === 'ventes' ? <FicheVentes soiree={soiree} recetteMasquee={fiche.recette_masquee} /> : null}
        </div>
      </div>
    </main>
  )
}
